import { useState, useEffect } from 'react';
import { 
  Facebook, 
  Save, 
  RefreshCw, 
  ToggleLeft, 
  ToggleRight, 
  DollarSign,
  TestTube,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FacebookPixelManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    pixel_id: '',
    enabled: false,
    events: []
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Default events if none configured
  const defaultEvents = [
    { event_name: 'Lead', enabled: true, currency: 'USD', value: 0.00, content_category: 'registration' },
    { event_name: 'CompleteRegistration', enabled: true, currency: 'USD', value: 0.00, content_category: 'signup' },
    { event_name: 'Login', enabled: true, currency: null, value: null, content_category: null },
    { event_name: 'PageView', enabled: true, currency: null, value: null, content_category: null },
    { event_name: 'InitiateCheckout', enabled: true, currency: 'USD', value: 0.00, content_category: 'registration' },
    { event_name: 'B2BLead', enabled: true, currency: 'USD', value: 0.00, content_category: 'b2b' },
  ];

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/pixel/config`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig({
          pixel_id: data.pixel_id || '',
          enabled: data.enabled || false,
          events: data.events?.length > 0 ? data.events : defaultEvents
        });
      } else {
        // Use defaults
        setConfig({
          pixel_id: '',
          enabled: false,
          events: defaultEvents
        });
      }
    } catch (error) {
      console.error('Failed to fetch pixel config:', error);
      toast.error('Failed to load pixel configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/admin/pixel/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Facebook Pixel configuration saved!');
        setHasChanges(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save pixel config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEvent = async () => {
    if (!config.pixel_id) {
      toast.error('Please enter a Pixel ID first');
      return;
    }

    try {
      setTesting(true);
      
      // Get Lead event config for test
      const leadEventConfig = config.events?.find(e => e.event_name === 'Lead') || {};
      const testCurrency = leadEventConfig.currency ?? 'USD';
      const testValue = typeof leadEventConfig.value === 'number' ? leadEventConfig.value : 0.00;
      
      // Fire a test event using the current pixel with proper parameters
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Test Event from Admin',
          content_category: 'test',
          currency: testCurrency,
          value: testValue
        });
        toast.success('Test event sent! Check your Facebook Events Manager.');
      } else {
        toast.error('Facebook Pixel not loaded. Make sure the pixel is enabled and refresh the page.');
      }
    } catch (error) {
      console.error('Failed to send test event:', error);
      toast.error('Failed to send test event');
    } finally {
      setTesting(false);
    }
  };

  const updatePixelId = (value) => {
    setConfig(prev => ({ ...prev, pixel_id: value }));
    setHasChanges(true);
  };

  const toggleEnabled = () => {
    setConfig(prev => ({ ...prev, enabled: !prev.enabled }));
    setHasChanges(true);
  };

  const updateEvent = (index, field, value) => {
    setConfig(prev => {
      const newEvents = [...prev.events];
      newEvents[index] = { ...newEvents[index], [field]: value };
      return { ...prev, events: newEvents };
    });
    setHasChanges(true);
  };

  const getEventDescription = (eventName) => {
    const descriptions = {
      'Lead': 'Fires on signup and high-intent actions',
      'CompleteRegistration': 'Fires when a user completes signup',
      'Login': 'Fires when a user logs in',
      'PageView': 'Fires on every page load',
      'InitiateCheckout': 'Fires when user starts signup flow',
      'B2BLead': 'Fires on B2B demo requests'
    };
    return descriptions[eventName] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-400" />
        <span className="ml-2 text-white/70">Loading pixel configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Facebook className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Facebook Pixel</h3>
            <p className="text-sm text-white/60">Manage your tracking pixel and event configuration</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={sendTestEvent}
            disabled={!config.pixel_id || !config.enabled || testing}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <TestTube className="w-4 h-4 mr-2" />
            )}
            Test Event
          </Button>
          <Button
            onClick={saveConfig}
            disabled={!hasChanges || saving}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Pixel ID & Global Toggle */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-white">Pixel Status</h4>
            <p className="text-sm text-white/60">Enable or disable all Facebook Pixel tracking</p>
          </div>
          <button
            onClick={toggleEnabled}
            className="flex items-center gap-2"
          >
            {config.enabled ? (
              <>
                <ToggleRight className="w-10 h-10 text-green-400" />
                <span className="text-green-400 font-medium">Enabled</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-10 h-10 text-white/40" />
                <span className="text-white/40 font-medium">Disabled</span>
              </>
            )}
          </button>
        </div>

        <div className="pt-4 border-t border-white/10">
          <label className="block text-sm font-medium text-white mb-2">
            Facebook Pixel ID
          </label>
          <div className="flex gap-2">
            <Input
              type="text"
              value={config.pixel_id}
              onChange={(e) => updatePixelId(e.target.value)}
              placeholder="Enter your Pixel ID (e.g., 1234567890)"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          <p className="mt-2 text-xs text-white/50 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Find your Pixel ID in Facebook Events Manager → Data Sources → Your Pixel
          </p>
        </div>
      </div>

      {/* Events Configuration */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <h4 className="font-medium text-white mb-4">Event Configuration</h4>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-white/60 border-b border-white/10">
                <th className="pb-3 pr-4">Event</th>
                <th className="pb-3 pr-4">Enabled</th>
                <th className="pb-3 pr-4">Currency</th>
                <th className="pb-3 pr-4">Value</th>
                <th className="pb-3">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {config.events.map((event, index) => (
                <tr key={event.event_name} className="text-white">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      {event.enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-white/30" />
                      )}
                      <span className="font-medium">{event.event_name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    <Switch
                      checked={event.enabled}
                      onCheckedChange={(checked) => updateEvent(index, 'enabled', checked)}
                    />
                  </td>
                  <td className="py-3 pr-4">
                    {event.currency !== null ? (
                      <select
                        value={event.currency || 'USD'}
                        onChange={(e) => updateEvent(index, 'currency', e.target.value)}
                        className="bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-white"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                      </select>
                    ) : (
                      <span className="text-white/40 text-sm">N/A</span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    {event.value !== null ? (
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-white/40" />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={event.value || 0}
                          onChange={(e) => updateEvent(index, 'value', parseFloat(e.target.value) || 0)}
                          className="w-20 bg-white/10 border-white/20 text-white text-sm py-1 px-2"
                        />
                      </div>
                    ) : (
                      <span className="text-white/40 text-sm">N/A</span>
                    )}
                  </td>
                  <td className="py-3">
                    <span className="text-sm text-white/50">
                      {getEventDescription(event.event_name)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Section */}
      <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
        <h4 className="font-medium text-blue-300 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4" />
          How to set up Facebook Pixel
        </h4>
        <ol className="text-sm text-blue-200/70 space-y-1 list-decimal list-inside">
          <li>Go to Facebook Events Manager → Data Sources</li>
          <li>Create a new Pixel or select an existing one</li>
          <li>Copy the Pixel ID (numeric value)</li>
          <li>Paste it above and enable tracking</li>
          <li>Use the Test Event button to verify it works</li>
          <li>Check Events Manager to see incoming events</li>
        </ol>
      </div>
    </div>
  );
};

export default FacebookPixelManager;
